import React, { useState, useCallback } from "react"
import { Phone, User, Users, Hash, Warning } from "@phosphor-icons/react"
import { toast } from "sonner"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../../ui/dialog"
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../../ui/tabs"
import { Button } from "../../ui/button"
import { Input } from "../../ui/input"
import { Label } from "../../ui/label"
import { UserSearch } from "../../ui/collection-search"
import { ContactSearch } from "../../ui/collection-search"
import { useCallStore } from "../../../stores"
import { api } from "../../../lib/api/client"
import { cn } from "../../../lib/utils"

interface UserData {
  _id: string
  display_name?: string
  given_name?: string
  family_name?: string
  phone?: string
  agent_can_receive_calls?: boolean
}

interface ContactData {
  _id: string
  display_name?: string
  given_name?: string
  family_name?: string
  phone?: string
  phone_2?: string
  phone_3?: string
}

interface TransferModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

// Format phone for display
function formatPhone(phone: string): string {
  if (!phone) return ""
  const cleaned = phone.replace(/\D/g, "")
  if (cleaned.length === 10) {
    return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`
  }
  if (cleaned.length === 11 && cleaned[0] === "1") {
    return `+1 (${cleaned.slice(1, 4)}) ${cleaned.slice(4, 7)}-${cleaned.slice(7)}`
  }
  return phone
}

export function TransferModal({ open, onOpenChange }: TransferModalProps) {
  const { currentCall, setTransferCall } = useCallStore()

  const [activeTab, setActiveTab] = useState<"user" | "contact" | "number">("user")
  const [isLoading, setIsLoading] = useState(false)

  // User tab state
  const [selectedUser, setSelectedUser] = useState<UserData | null>(null)
  const [userCallOption, setUserCallOption] = useState<"direct" | "cell">("direct")

  // Contact tab state
  const [selectedContact, setSelectedContact] = useState<ContactData | null>(null)
  const [selectedContactPhone, setSelectedContactPhone] = useState<string>("")

  // Number tab state
  const [phoneNumber, setPhoneNumber] = useState("")

  const resetForm = useCallback(() => {
    setSelectedUser(null)
    setUserCallOption("direct")
    setSelectedContact(null)
    setSelectedContactPhone("")
    setPhoneNumber("")
    setActiveTab("user")
  }, [])

  const handleClose = useCallback(() => {
    resetForm()
    onOpenChange(false)
  }, [resetForm, onOpenChange])

  const handleAddCall = useCallback(async () => {
    let callSid = currentCall?.call_sid || ""
    if (!callSid) {
      // Wait briefly for socket to deliver the call_sid (outbound call reconciliation)
      await new Promise((r) => setTimeout(r, 1500))
      callSid = useCallStore.getState().currentCall?.call_sid || ""
      if (!callSid) {
        toast.error("Unable to transfer — please try again")
        return
      }
    }

    let params: {
      type: "user" | "contact" | "number"
      user?: string | null
      userNumber?: string
      contact?: string | null
      contactNumber?: string
      number: string
      callSid: string
    } = {
      type: activeTab,
      number: "",
      callSid,
    }

    let displayName = ""

    if (activeTab === "user") {
      if (!selectedUser) {
        toast.info("Please select a user to transfer to")
        return
      }
      params.user = selectedUser._id
      params.userNumber = userCallOption === "cell" && selectedUser.phone
        ? selectedUser.phone
        : selectedUser._id
      displayName = selectedUser.display_name || `${selectedUser.given_name || ""} ${selectedUser.family_name || ""}`.trim()
    } else if (activeTab === "contact") {
      if (!selectedContact || !selectedContactPhone) {
        toast.info("Please select a contact with a phone number")
        return
      }
      params.contact = selectedContact._id
      params.contactNumber = selectedContactPhone
      displayName = selectedContact.display_name || `${selectedContact.given_name || ""} ${selectedContact.family_name || ""}`.trim() || selectedContactPhone
    } else if (activeTab === "number") {
      if (!phoneNumber || phoneNumber.length < 10) {
        toast.info("Please enter a valid phone number")
        return
      }
      let formattedNumber = phoneNumber.replace(/\D/g, "")
      if (!formattedNumber.startsWith("1") && formattedNumber.length === 10) {
        formattedNumber = "1" + formattedNumber
      }
      params.number = "+" + formattedNumber
      displayName = formatPhone(params.number)
    }

    setIsLoading(true)

    try {
      const response = await api.callCenter.addCall(params)

      if (!response.success) {
        toast.error(
          "Transfer could not be completed. If you entered a phone number, please verify it is a valid US phone number."
        )
        setIsLoading(false)
        return
      }

      // Set transfer state
      setTransferCall({
        _id: selectedUser?._id || selectedContact?._id || params.number,
        display_name: displayName,
        type: activeTab,
        can_merge: false,
      })

      handleClose()
    } catch (error) {
      console.error("Transfer error:", error)
      toast.error("Failed to initiate transfer")
    } finally {
      setIsLoading(false)
    }
  }, [
    activeTab,
    currentCall,
    selectedUser,
    userCallOption,
    selectedContact,
    selectedContactPhone,
    phoneNumber,
    setTransferCall,
    handleClose,
  ])

  // Handle user selection
  const handleUserChange = useCallback((option: { value: string; data: UserData } | null) => {
    if (option) {
      setSelectedUser(option.data)
      setUserCallOption("direct")
    } else {
      setSelectedUser(null)
    }
  }, [])

  // Handle contact selection
  const handleContactChange = useCallback((option: { value: string; data: ContactData } | null) => {
    if (option) {
      setSelectedContact(option.data)
      // Auto-select first available phone
      if (option.data.phone) {
        setSelectedContactPhone(option.data.phone)
      } else if (option.data.phone_2) {
        setSelectedContactPhone(option.data.phone_2)
      } else if (option.data.phone_3) {
        setSelectedContactPhone(option.data.phone_3)
      } else {
        setSelectedContactPhone("")
      }
    } else {
      setSelectedContact(null)
      setSelectedContactPhone("")
    }
  }, [])

  const hasUserCell = selectedUser?.phone?.includes("+1")

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Phone className="h-5 w-5" />
            Add Call / Transfer
          </DialogTitle>
        </DialogHeader>

        <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as typeof activeTab)}>
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="user" className="flex items-center gap-1.5">
              <User className="h-3.5 w-3.5" />
              <span className="hidden sm:inline">User</span>
            </TabsTrigger>
            <TabsTrigger value="contact" className="flex items-center gap-1.5">
              <Users className="h-3.5 w-3.5" />
              <span className="hidden sm:inline">Contact</span>
            </TabsTrigger>
            <TabsTrigger value="number" className="flex items-center gap-1.5">
              <Hash className="h-3.5 w-3.5" />
              <span className="hidden sm:inline">Number</span>
            </TabsTrigger>
          </TabsList>

          {/* User Tab */}
          <TabsContent value="user" className="space-y-4">
            <div className="space-y-2">
              <Label>Select User</Label>
              <UserSearch
                value={selectedUser?._id || null}
                onChange={handleUserChange}
                placeholder="Search for a user..."
                filter={{ removeSelf: true }}
              />
            </div>

            {selectedUser && (
              <div className="rounded-md border bg-muted/50 p-3 space-y-2">
                <p className="text-sm font-medium">Transfer to:</p>
                <div className="space-y-2">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="userCallOption"
                      checked={userCallOption === "direct"}
                      onChange={() => setUserCallOption("direct")}
                      className="h-4 w-4 text-primary"
                    />
                    <span className="text-sm">
                      {selectedUser.display_name || selectedUser.given_name} (Direct)
                    </span>
                    {selectedUser.agent_can_receive_calls && (
                      <span className="ml-auto text-xs text-success">Online</span>
                    )}
                  </label>

                  {hasUserCell && (
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="userCallOption"
                        checked={userCallOption === "cell"}
                        onChange={() => setUserCallOption("cell")}
                        className="h-4 w-4 text-primary"
                      />
                      <span className="text-sm">
                        Cell Phone ({formatPhone(selectedUser.phone!)})
                      </span>
                    </label>
                  )}
                </div>
              </div>
            )}
          </TabsContent>

          {/* Contact Tab */}
          <TabsContent value="contact" className="space-y-4">
            <div className="space-y-2">
              <Label>Select Contact</Label>
              <ContactSearch
                value={selectedContact?._id || null}
                onChange={handleContactChange}
                placeholder="Search for a contact..."
              />
            </div>

            {selectedContact && (
              <div className="rounded-md border bg-muted/50 p-3 space-y-2">
                {selectedContact.phone || selectedContact.phone_2 || selectedContact.phone_3 ? (
                  <>
                    <p className="text-sm font-medium">Select phone number:</p>
                    <div className="space-y-2">
                      {selectedContact.phone && (
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="radio"
                            name="contactPhone"
                            checked={selectedContactPhone === selectedContact.phone}
                            onChange={() => setSelectedContactPhone(selectedContact.phone!)}
                            className="h-4 w-4 text-primary"
                          />
                          <span className="text-sm">
                            {formatPhone(selectedContact.phone)} - Main
                          </span>
                        </label>
                      )}
                      {selectedContact.phone_2 && (
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="radio"
                            name="contactPhone"
                            checked={selectedContactPhone === selectedContact.phone_2}
                            onChange={() => setSelectedContactPhone(selectedContact.phone_2!)}
                            className="h-4 w-4 text-primary"
                          />
                          <span className="text-sm">
                            {formatPhone(selectedContact.phone_2)} - Secondary
                          </span>
                        </label>
                      )}
                      {selectedContact.phone_3 && (
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="radio"
                            name="contactPhone"
                            checked={selectedContactPhone === selectedContact.phone_3}
                            onChange={() => setSelectedContactPhone(selectedContact.phone_3!)}
                            className="h-4 w-4 text-primary"
                          />
                          <span className="text-sm">
                            {formatPhone(selectedContact.phone_3)} - Other
                          </span>
                        </label>
                      )}
                    </div>
                  </>
                ) : (
                  <div className="flex items-center gap-2 text-warning">
                    <Warning className="h-4 w-4" />
                    <span className="text-sm">This contact has no phone number on file.</span>
                  </div>
                )}
              </div>
            )}
          </TabsContent>

          {/* Number Tab */}
          <TabsContent value="number" className="space-y-4">
            <div className="space-y-2">
              <Label>Phone Number</Label>
              <Input
                type="tel"
                placeholder="(555) 555-5555"
                value={phoneNumber}
                onChange={(e) => setPhoneNumber(e.target.value)}
                className="font-mono"
              />
              <p className="text-xs text-muted-foreground">
                Enter a US phone number to transfer the call to.
              </p>
            </div>
          </TabsContent>
        </Tabs>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose} disabled={isLoading}>
            Cancel
          </Button>
          <Button onClick={handleAddCall} disabled={isLoading}>
            {isLoading ? "Transferring..." : "Add Call"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
